/**
 * Central enum code -> human label registry.
 *
 * The backend serialises every enum via `@JsonValue` on its CODE (e.g. UserType -> "C",
 * PostStatus -> "HID"), and orval generates those codes as plain string unions. The React app
 * therefore owns the code->label mapping. Values mirror the backend enums in
 * tipmi-api/core `com.tipmi.core.constant.*` — keep them in sync when the backend changes.
 *
 * Editing the core enum serialisation instead was rejected on purpose: core ships as a published
 * Maven jar consumed by the mobile app too, so a serialisation change is a platform-wide risk.
 */

export type EnumMap = Readonly<Record<string, string>>;

/** Title-case a raw code as a last-resort label (SPAM -> "Spam", welcome_message -> "Welcome Message"). */
function titleCase(code: string): string {
  return code
    .replace(/[_-]+/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Resolve a backend code to its label. Unknown codes fall back to a title-cased version of the
 * code itself (never a bare "Unknown"), and null/empty falls back to `empty` (default em dash).
 */
export function enumLabel(
  map: EnumMap,
  code: string | null | undefined,
  empty = '—',
): string {
  if (code == null || code === '') return empty;
  return map[code] ?? map[code.toUpperCase()] ?? titleCase(code);
}

/** Build DataGrid filter options ({key,label}) from a map, in declaration order. */
export function enumOptions(map: EnumMap): { key: string; label: string }[] {
  return Object.entries(map).map(([key, label]) => ({ key, label }));
}

// ─── User ────────────────────────────────────────────────────────────────────
/** UserType — N/C/A. */
export const USER_TYPE: EnumMap = { N: 'Normal', C: 'Creator', A: 'Admin' };

/** UserStatus — ACT/INA/TER. */
export const USER_STATUS: EnumMap = { ACT: 'Active', INA: 'Inactive', TER: 'Terminated' };

/** AccountStatus (admin accounts) — NEW/ACT/TER. */
export const ACCOUNT_STATUS: EnumMap = { NEW: 'New', ACT: 'Active', TER: 'Terminated' };

/** AccountVerification (KYC) — VR/NVR. */
export const ACCOUNT_VERIFICATION: EnumMap = { VR: 'Verified', NVR: 'Not Verified' };

/** GenderType — M/F/N. */
export const GENDER: EnumMap = { M: 'Male', F: 'Female', N: 'Unknown' };

/**
 * AuthProviderType — provider code / OAuth domain -> friendly name (drives loginMethod).
 *
 * `user_identity_mapping.authentication_method` is what actually reaches the UI. The backend's own
 * `StatisticsServiceImpl#signupMethodLabel` (admin-api) switches on the lower-cased values
 * apple/email/phone/google/facebook/twitter, so those are listed here alongside the legacy short
 * codes and OAuth domains that older rows still carry.
 */
export const AUTH_PROVIDER: EnumMap = {
  phone: 'Phone Number',
  email: 'Email',
  google: 'Google',
  apple: 'Apple',
  facebook: 'Facebook',
  twitter: 'X',
  GG: 'Google',
  FB: 'Facebook',
  TWT: 'Twitter',
  TM: 'Tipmi',
  'google.com': 'Google',
  'facebook.com': 'Facebook',
  'twitter.com': 'Twitter',
  'tipmi.com': 'Tipmi',
};

/**
 * Login Type filter catalogue (TM-3208 / TM-3211) — the providers users can actually sign up
 * with, and the exact `authentication_method` codes the server filters on. Facebook and X are
 * deliberately absent: sign-up never supported them, and no row carries those values.
 *
 * Fixed rather than derived from the loaded rows because the filter runs server-side, so the
 * options have to cover providers that no loaded page happens to contain.
 */
export const LOGIN_TYPE_FILTER_OPTIONS: { key: string; label: string }[] = [
  { key: 'phone', label: 'Phone Number' },
  { key: 'email', label: 'Email' },
  { key: 'google', label: 'Google' },
  { key: 'apple', label: 'Apple' },
];

/**
 * loginMethod arrives already capitalised by the backend DTO getter, or as a provider code/domain.
 * Try the provider map first, else return the capitalised value as-is.
 */
export function loginMethodLabel(value: string | null | undefined, empty = '—'): string {
  if (value == null || value === '') return empty;
  return AUTH_PROVIDER[value] ?? AUTH_PROVIDER[value.toUpperCase()] ?? AUTH_PROVIDER[value.toLowerCase()] ?? value;
}

// ─── Content / Post ──────────────────────────────────────────────────────────
/** PostType (content list) — N/I/E/S/M/L/F/W. Distinct from ContentType below. N renders as
 *  "Post" (a normal post) per CMP ContentDisplayUtils, not the enum name "Normal". */
export const POST_TYPE: EnumMap = {
  N: 'Post',
  I: 'Images',
  E: 'Exclusive',
  S: 'Story',
  M: 'Message',
  L: 'Live',
  F: 'Flicks',
  W: 'Welcome Message',
};

/** PostStatus — PUB/WAT/EXP/HID/TER. WAT = TipMi pre-publish hold (Waiting); Coop is separate. */
export const POST_STATUS: EnumMap = {
  PUB: 'Published',
  WAT: 'Waiting',
  EXP: 'Expired',
  HID: 'Hidden',
  TER: 'Terminated',
};

/** ContentType (report discriminator) — P/C/U/B/S/H/F/M/L. Distinct from PostType. */
export const CONTENT_TYPE: EnumMap = {
  P: 'Post',
  C: 'Comment',
  U: 'User',
  B: 'Feedback',
  S: 'Story',
  H: 'Highlight',
  F: 'Flicks',
  M: 'Message',
  L: 'Live',
};

/** CollaborationStatus — APPROVED/DENIED/PENDING. */
export const COLLABORATION_STATUS: EnumMap = {
  APPROVED: 'Approved',
  DENIED: 'Denied',
  PENDING: 'Pending',
};

/** MediaFile type — VID/IMG/AUD. */
export const FILE_TYPE: EnumMap = { VID: 'Video', IMG: 'Image', AUD: 'Audio' };

// ─── Moderation ──────────────────────────────────────────────────────────────
/** UserReportType — full report-reason catalogue. */
export const USER_REPORT_TYPE: EnumMap = {
  SPAM: 'Report Spam',
  NOSA: 'Nudity or sexual activity',
  HSOS: 'Hate speech or symbols',
  VODO: 'Violence or dangerous organizations',
  SOIORG: 'Sale of illegal or regulated goods',
  BOH: 'Bullying or harassment',
  IPV: 'Intellectual property violation',
  SOS: 'Suicide or self-injury',
  ED: 'Eating disorders',
  SOF: 'Scam or fraud',
  DRUGS: 'Drugs',
  FI: 'False information',
  AMHBH: 'Account may have been hacked',
};

/** ReportStatus — serialised as NAME (no @JsonValue). */
export const REPORT_STATUS: EnumMap = { PENDING: 'Pending', COMPLETED: 'Completed' };

// ─── Payments / statistics ───────────────────────────────────────────────────
/** TransactionType — FUND/RECHARGE/TIP/... */
export const TRANSACTION_TYPE: EnumMap = {
  FUND: 'Add funds',
  RECHARGE: 'Auto recharge',
  TIP: 'Send tip',
  EXCLUSIVE: 'Pay exclusive',
  MESSAGE: 'Pay message',
  SUBSCRIBE: 'Subscribe',
  REFERRAL: 'Referral',
  GST: 'GST',
  LIVESTREAM: 'Stream',
};

/** PaymentMethodSenderType — WALLET/CARD/APPLE/... */
export const PAYMENT_SENDER_TYPE: EnumMap = {
  WALLET: 'Wallet',
  CARD: 'Card',
  APPLE: 'Apple Pay',
  PAYPAL: 'PayPal',
  IOS: 'In-app purchase',
  REFER: 'Refer earning',
  GST: 'GST',
};

/** SubscriptionStatus — NONE/SUB_AVAIL/SUB_TERM/SUB_END. */
export const SUBSCRIPTION_STATUS: EnumMap = {
  NONE: 'Not subscribed',
  SUB_AVAIL: 'Subscribing',
  SUB_TERM: 'Auto-renew canceled',
  SUB_END: 'Ended',
};

// ─── Convenience label helpers (thin wrappers over enumLabel) ────────────────
export const userTypeLabel = (c?: string | null) => enumLabel(USER_TYPE, c);
export const userStatusLabel = (c?: string | null) => enumLabel(USER_STATUS, c);
export const accountStatusLabel = (c?: string | null) => enumLabel(ACCOUNT_STATUS, c);
export const accountVerificationLabel = (c?: string | null) => enumLabel(ACCOUNT_VERIFICATION, c);
export const genderLabel = (c?: string | null) => enumLabel(GENDER, c, 'Not Set');
export const postTypeLabel = (c?: string | null) => enumLabel(POST_TYPE, c);
export const postStatusLabel = (c?: string | null) => enumLabel(POST_STATUS, c);
export const contentTypeLabel = (c?: string | null) => enumLabel(CONTENT_TYPE, c);
export const userReportTypeLabel = (c?: string | null) => enumLabel(USER_REPORT_TYPE, c);
export const transactionTypeLabel = (c?: string | null) => enumLabel(TRANSACTION_TYPE, c);
export const paymentSenderLabel = (c?: string | null) => enumLabel(PAYMENT_SENDER_TYPE, c);
